import { Phone, Clock, Check } from "lucide-react";
import { Order } from "@/hooks/useOrders";
import { formatPhoneNumber } from "@/utils/phoneFormatter";
import { formatTime, formatTimeUTC } from "@/utils/orderUtils";

interface OrderIdentityProps {
  order: Order;
  /** "full" for the list OrderCard, "compact" for the dense By-Time rows. */
  variant?: "full" | "compact";
}

/**
 * The order-identity header: order number, claimed chip, tap-to-call phone,
 * promise time and payment status. Shared by OrderCard and TimeGroupedOrdersView
 * so the two views cannot drift on how an order identifies itself.
 */
export function OrderIdentity({ order, variant = "full" }: OrderIdentityProps) {
  const claimed = !!order.offer_claimed;
  const isPaid = order.payment_status === "Paid";
  const cleanPhone = order.customer_phone?.replace(/[^\d+]/g, "") ?? "";

  if (variant === "compact") {
    return (
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h4 className="font-semibold text-foreground tabular-nums">
              Order #{order.order_no || order.order_id}
            </h4>
            {claimed && (
              <span className="inline-flex items-center gap-1 rounded-full bg-status-success/15 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-status-success">
                <Check className="h-3 w-3" aria-hidden="true" />
                Claimed
              </span>
            )}
          </div>
          {order.customer_phone ? (
            <a
              href={`tel:${cleanPhone}`}
              className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-status-info hover:underline"
            >
              <Phone className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
              <span className="tabular-nums">{formatPhoneNumber(order.customer_phone)}</span>
            </a>
          ) : (
            <p className="mt-1 text-sm font-medium text-muted-foreground">No phone number</p>
          )}
          <p className="mt-0.5 text-xs text-muted-foreground">{order.order_type || "Unknown type"}</p>
        </div>
        <div className="flex-none text-right">
          <p className="flex items-center justify-end gap-1 text-sm font-semibold text-foreground tabular-nums">
            <Clock className="h-3.5 w-3.5 flex-none text-muted-foreground" aria-hidden="true" />
            {formatTime(order.promise_time)}
          </p>
          <p className={`mt-0.5 text-xs font-semibold ${isPaid ? "text-status-success" : "text-status-danger"}`}>
            {order.payment_status}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-start justify-between gap-3">
        {/* Identifiers first: order number + customer phone read at a glance. */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="text-lg sm:text-xl font-bold text-foreground tabular-nums">
              Order #{order.order_no || order.order_id}
            </h3>
            {claimed && (
              <span className="inline-flex items-center gap-1 rounded-full bg-status-success/15 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-status-success">
                <Check className="h-3 w-3" aria-hidden="true" />
                Offer claimed
              </span>
            )}
          </div>

          {order.customer_phone ? (
            <a
              href={`tel:${cleanPhone}`}
              className="mt-1.5 inline-flex items-center gap-1.5 text-base font-semibold text-status-info hover:underline"
            >
              <Phone className="h-4 w-4 flex-none" aria-hidden="true" />
              <span className="tabular-nums">{formatPhoneNumber(order.customer_phone)}</span>
            </a>
          ) : (
            <p className="mt-1.5 text-base font-semibold text-muted-foreground">No phone number</p>
          )}
        </div>

        {/* The operational deadline: prominent by weight, calm by color. */}
        {order.promise_time && (
          <div className="flex-none text-right">
            <p className="text-[0.6875rem] font-semibold uppercase tracking-wide text-muted-foreground">
              Promise
            </p>
            <p className="mt-0.5 flex items-center justify-end gap-1 text-base sm:text-lg font-semibold text-foreground tabular-nums">
              <Clock className="h-4 w-4 flex-none text-muted-foreground" aria-hidden="true" />
              {formatTime(order.promise_time)}
            </p>
          </div>
        )}
      </div>

      {/* Everything else is metadata: quiet, wrapping, never competing. */}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span className={`font-semibold ${isPaid ? "text-status-success" : "text-status-danger"}`}>
          {order.payment_status}
        </span>
        <span aria-hidden="true">·</span>
        <span>{order.order_type || "Unknown type"}</span>
        <span aria-hidden="true">·</span>
        <span className="tabular-nums">Ordered {formatTime(order.created_at)}</span>
        {order.taken_by && (
          <>
            <span aria-hidden="true">·</span>
            <span>Taken by {order.taken_by}</span>
          </>
        )}
        {order.is_posted && order.posted_time && (
          <>
            <span aria-hidden="true">·</span>
            <span className="tabular-nums">Done {formatTimeUTC(order.posted_time)}</span>
          </>
        )}
        {order.is_posted && order.posted_by_name && (
          <>
            <span aria-hidden="true">·</span>
            <span>Posted by {order.posted_by_name}</span>
          </>
        )}
        <span aria-hidden="true">·</span>
        <span className="tabular-nums">ID {order.order_id}</span>
      </div>
    </>
  );
}

export default OrderIdentity;
